import { create } from 'zustand';
import { toast } from 'react-toastify';
import { outagesMapCenter } from './mapUtils';
import ApiErrorNotification from './notifications/ApiErrorNotification';
import type { Revalidator } from 'swr';

export type FeatureStatus = 'Active' | 'Completed' | 'Registered' | 'Major' | 'Closed';
export type DisplayItem = "filter" | "search" | "legend" | null;

export type Feature = {
  id: number;
  data: MapFeature['properties'],
  marker: google.maps.marker.AdvancedMarkerElement,
  polygon: google.maps.Polygon
}
export type MarkerInView = {
  feature: any;
  position: google.maps.LatLngLiteral;
}

export interface MapFeature {
    type: 'Feature';
    geometry: {
        type: 'Point' | 'Polygon';
        coordinates: number[] | number[][][];
    };
    properties: {
        id: string;
        status: FeatureStatus;
        affected_suburbs: Array<string>;
        type: string;
        name: string;
        start_date: string;
        end_date: string;
        estimated_off?: string;
        estimated_on?: string;
        planned_off?: string;
        planned_on?: string;
        actual_on?: string;
        actual_off?: string;
        start_date_interruption?: string;
        end_date_interruption?: string;
        position: google.maps.LatLngLiteral | google.maps.LatLng;
        region?: string;
        town?: string;
        suburb?: string;
        affected_customers_outage?: number;
        sensitive_customers?: number;
        key_customers?: number;
        extension_status?: string;
        work_order_number?:string;
        notes?: string;
        planned_flag: "True" | "False";
        extension?: string;
        outagesFormUrl?: string;
    };
}

export interface MapState {
    id: number;
    hasData: {mapData: boolean; eventData: boolean};
    map?: google.maps.Map;
    mapReady: boolean;
    mapLoadError: boolean;
    features: Map<number, Feature>;
    selectedFeature: Feature | null;
    filterStatus: {
        Active: boolean
        Registered: boolean
        Major: boolean
        Completed: boolean
        Closed: boolean
    },
    display: DisplayItem;
    isDesktop: boolean;
    searchPlace: string;
    sidePanelWidth: number;
    visibleFeatures: {
        key: number;
        data: Feature["data"];
    }[];
    incrementId: () => void;
    resetMapView: () => void
    setDisplay: (displayItem: DisplayItem) => void;
    setIsDesktop: (num: number) => void;
    setSearchPlace: (place: string) => void;
    setMap: (map: google.maps.Map) => void;
    setMapLoadError: () => void;
    setMapReady: () => void;
    setFeatures: (features: Feature[], type: 'mapData' | 'eventData') => void;
    setSelectedFeature: (id: number) => void;
    setFilter: (filterBy: FeatureStatus, status: boolean) => void;
    setVisibleFeatures: () => void;
}

export const useMapStore = create<MapState>((set) => ({
    id: 0,
    hasData: {mapData: false, eventData: false},
    mapReady: false,
    mapLoadError: false,
    features: new Map(),
    selectedFeature: null,
    markersInView: [],
    filterStatus: {
        Active: true,
        Registered: true,
        Major: true,
        Completed: true,
        Closed: true,
    },
    display: null,
    isDesktop: true,
    searchPlace: "",
    sidePanelWidth: 380,
    visibleFeatures: [],
    incrementId: () => {
      set(({id}) => ({id: id + 1}))
    },
    resetMapView: () => {
        set({
            display: null,
            filterStatus: {
                Active: true,
                Registered: true,
                Major: true,
                Completed: true,
                Closed: true,
            }
        })
        resetMap()
    },
    setDisplay: (displayItem: DisplayItem) => {
        set({display: displayItem})
    },
    setIsDesktop: (num) => {
        let isDesktop = num >= 768
        let sidePanelWidth = (isDesktop || num * 0.8 >= 380) ? 380 : num * 0.8
        set({isDesktop, sidePanelWidth})
    },
    setSearchPlace: (place) => {set({ searchPlace: place })},
    setMap: (map) => set({ map }),
    setMapLoadError: () => {
      set({mapLoadError: true})
      toast.dismiss();
    },
    setMapReady: () => {set({mapReady: true})},
    setFeatures: (f, t) => set(({ features, hasData, resetMapView }) => {
        if((t === 'eventData' && hasData.eventData) || (t === 'mapData' && hasData.mapData)) {
          return ({ features })
        }
        f.forEach((x) => {
            x.data.position = x.marker.position as google.maps.LatLngLiteral
            features.set(x.id, x)
        })
        setTimeout(() => {resetMapView()})
        return ({ features, hasData: { mapData: t === 'mapData' ? true: hasData.mapData, eventData: t === 'eventData' ? true : hasData.eventData } })
    }),
    setSelectedFeature: (id) => {
        set(({ features }) => ({ selectedFeature: features.get(id) ?? null }))
    },
    setFilter: (filterBy: FeatureStatus, status: boolean) => {
        set(({ filterStatus }) => {
            return { filterStatus: {...filterStatus, [filterBy]: status} }
        })
        set(() => {
            return ({ visibleFeatures: getVisibleMarkersInBounds() })
        })
    },
    setVisibleFeatures: () => set(() => {
        return ({ visibleFeatures: getVisibleMarkersInBounds() })
    }),
}));

// toast store
interface ToastState {
  mapDataToastId: string | number | null;
  eventDataToastId: string | number | null;
  mapDataError: any | null;
  eventDataError: any | null;
  
  // Actions
  showLoadingToast: (type: 'map' | 'event') => void;
  dismissToast: (type: 'map' | 'event') => void;
  updateToastWithRetry: (type: 'map' | 'event', retryCount: number) => void;
  showErrorToast: (type: 'map' | 'event', error: any, revalidate?: Revalidator) => void;
  clearErrors: () => void;
}

export const useToastStore = create<ToastState>((set, get) => ({
  mapDataToastId: null,
  eventDataToastId: null,
  mapDataError: null,
  eventDataError: null,

  showLoadingToast: (type) => {
    const toastData = get()
    if(toastData[`${type}DataToastId`]) return
    const id = toast.loading("Please wait...", {
      type: 'info',
      position: 'top-left',
      className: 'bg-secondary text-white',
    });
    set({ 
      [`${type}DataToastId`]: id,
      [`${type}DataError`]: null 
    });
  },

  dismissToast: (type) => {
    const toastId = get()[`${type}DataToastId`];
    if (toastId) {
      toast.dismiss(toastId);
      set({ 
        [`${type}DataToastId`]: null,
        [`${type}DataError`]: null 
      });
    }
  },

  updateToastWithRetry: (type, retryCount) => {
    const toastId = get()[`${type}DataToastId`];
    if (toastId) {
      toast.update(toastId, {
        type: 'info',
        render: `Fetching ${type} data! Retrying... (${retryCount}/3)`,
        ariaLabel: `New notification, Fetching ${type} data, attempt ${retryCount} of 3`,
        className: 'bg-secondary text-white',
        isLoading: true,
        position: 'top-left',
        autoClose: false,
        closeButton: false
      });
    }
  },

  showErrorToast: (type, error, revalidate) => {
    const isMap = type === 'map';
    const toastId = get()[`${type}DataToastId`];
    const otherError = get()[`${isMap ? 'event': 'map'}DataError`];
    if (toastId) {
      toast.dismiss(toastId);
      set({ 
        [`${type}DataToastId`]: null,
        [`${type}DataError`]: error 
      });
      if(otherError) {
        toast.dismiss()
        return
      }
      if(revalidate) {
        toast.error(ApiErrorNotification, {
          data: {
            title: `${isMap ? 'Map data' : 'Events data'} error:`,
            content: 'Data unavailable!',
            action: revalidate,
          },
          ariaLabel: `New error notification, ${type} data not available at this time`,
          autoClose: false,
          icon: false,
          theme: 'colored',
        });
      } else {
        toast.error(`${isMap ? 'Outages' : 'Events'} data unavailable!`, {
          ariaLabel: `New error notification, ${type} data not available at this time`,
          position: 'top-left',
          closeOnClick: true,
          closeButton: true,
          autoClose: 8000
        });
      }
    }
  },

  clearErrors: () => {
    set({ 
      mapDataError: null, 
      eventDataError: null 
    });
  }
}));

// store helper functions
export const getFeature = (id: number) => {
    const { features } = useMapStore.getState();
    if (features.has(id)) {
        return features.get(id)
    }
}

export const getId = () => {
  const { id, incrementId } = useMapStore.getState();
  incrementId();
  return id;
}

export const resetMap = () => {
    const { map } = useMapStore.getState();
    map?.setCenter(outagesMapCenter)
    map?.setZoom(5)
};

export const getVisibleMarkersInBounds = () => {
    const { map, features, filterStatus } = useMapStore.getState();
    if (!map || !features.size) return [];

    const bounds = map.getBounds();
    if (!bounds) return [];

    const inBoundsMarkers: { key: number, data: Feature['data'] }[] = [];
    features.forEach((feature, key) => {
        const isVisible = filterStatus[feature.data.status] ?? false
        if (isVisible && feature.marker.position && bounds.contains(feature.marker.position)) {
            inBoundsMarkers.push({ key, data: feature.data });
        }
    });

    return inBoundsMarkers;
};
